import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { Box, Grid, Typography } from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import CurrencyCard from "../components/currencies/CurrencyCard";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { getCurrencies } from "../redux/actions/currencyActions";
import PageError from "../components/common/PageError";

const CurrenciesPage: React.FC = () => {
  const dispatch = useAppDispatch();

  // Redux State
  const {
    items: currencies,
    loading,
    error,
  } = useAppSelector((state) => state.currency);

  // Local State for client-side search
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch Data on mount
  const fetchCurrencies = useCallback(() => {
    dispatch(getCurrencies());
  }, [dispatch]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  /**
   * Client-side Filtering Logic
   * Filters the full list based on Code or Name
   */
  const filteredCurrencies = useMemo(() => {
    if (!currencies) return [];
    if (!searchTerm.trim()) return currencies;

    const query = searchTerm.toLowerCase();
    return currencies.filter(
      (c) =>
        c.code.toLowerCase().includes(query) ||
        c.name.toLowerCase().includes(query),
    );
  }, [currencies, searchTerm]);

  const renderContent = (): React.JSX.Element => {
    if (loading) return <AppCircularProgress />;

    if (error) return <PageError error={error} onRetry={fetchCurrencies} />;

    if (filteredCurrencies.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography color="text.secondary">
            {searchTerm
              ? `No results found for "${searchTerm}"`
              : "No currencies available."}
          </Typography>
        </Box>
      );

    return (
      <Grid container spacing={3}>
        {filteredCurrencies.map((currency) => (
          <Grid key={currency.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <CurrencyCard currency={currency} />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="System Currencies"
      subtitle="View supported global currencies for financial transactions."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by code or name (e.g. USD or Dollar)..."
      />

      {renderContent()}
    </PageContainer>
  );
};

export default CurrenciesPage;
